import { IOwnerPropertyOption, ISubAccount } from "../types";

function asFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asNullableString(value: unknown): string | null {
  const text = String(value ?? "").trim();
  return text || null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function extractPropertyIds(record: Record<string, unknown>): number[] {
  if (Array.isArray(record.property_ids)) {
    return record.property_ids
      .map((id) => asFiniteNumber(id))
      .filter((id): id is number => id != null);
  }

  if (Array.isArray(record.properties)) {
    return record.properties
      .map((property) => {
        const propertyRecord = asRecord(property);
        return propertyRecord ? asFiniteNumber(propertyRecord.id) : null;
      })
      .filter((id): id is number => id != null);
  }

  return [];
}

function resolveUserFields(record: Record<string, unknown>): {
  firstName: string;
  email: string;
  phone: string;
} {
  // OwnerSubAccount serializes identity under the sub_user relation.
  const nestedUser =
    asRecord(record.sub_user) ??
    asRecord(record.subUser) ??
    asRecord(record.user);

  return {
    firstName:
      asNullableString(nestedUser?.first_name) ??
      asNullableString(record.first_name) ??
      asNullableString(record.name) ??
      "",
    email:
      asNullableString(nestedUser?.email) ??
      asNullableString(record.email) ??
      "",
    phone:
      asNullableString(nestedUser?.phone) ??
      asNullableString(record.phone) ??
      "",
  };
}

export function normalizeSubAccount(value: unknown): ISubAccount | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = asFiniteNumber(record.id);

  if (id == null) {
    return null;
  }

  const { firstName, email, phone } = resolveUserFields(record);
  const active =
    typeof record.is_active === "boolean"
      ? record.is_active
      : typeof record.active === "boolean"
        ? record.active
        : true;

  return {
    id,
    first_name: firstName,
    email,
    phone,
    active,
    property_ids: extractPropertyIds(record),
    created_at: asNullableString(record.created_at),
    // Missing flag means a dedicated sub-login account (fully editable).
    created_for_sub_login: record.created_for_sub_login !== false,
  };
}

export function normalizeSubAccountsList(raw: unknown): ISubAccount[] {
  const payload =
    raw && typeof raw === "object" && "data" in (raw as object)
      ? (raw as { data: unknown }).data
      : raw;

  const list = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === "object" &&
        Array.isArray((payload as { items?: unknown }).items)
      ? (payload as { items: unknown[] }).items
      : [];

  return list
    .map(normalizeSubAccount)
    .filter((item): item is ISubAccount => item != null);
}

export function normalizeOwnerPropertyOption(
  value: unknown
): IOwnerPropertyOption | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = asFiniteNumber(record.id);

  if (id == null) {
    return null;
  }

  const title =
    asNullableString(record.title) ??
    asNullableString(record.name) ??
    `Imóvel ${id}`;
  const address = asRecord(record.address);
  const addressLabel = address
    ? [
        asNullableString(address.address),
        asNullableString(address.neighborhood),
        asNullableString(address.city),
      ]
        .filter(Boolean)
        .join(" — ")
    : asNullableString(record.address);
  const parentPropertyId =
    record.parent_property_id == null
      ? null
      : asFiniteNumber(record.parent_property_id);

  return {
    id,
    title,
    label: addressLabel ? `${title} (${addressLabel})` : title,
    parent_property_id: parentPropertyId,
  };
}

export function normalizeOwnerPropertiesList(
  raw: unknown
): IOwnerPropertyOption[] {
  const payload =
    raw && typeof raw === "object" && "data" in (raw as object)
      ? (raw as { data: unknown }).data
      : raw;

  const list = Array.isArray(payload) ? payload : [];

  return list
    .map(normalizeOwnerPropertyOption)
    .filter((item): item is IOwnerPropertyOption => item != null);
}
